import React, { useState, useEffect } from 'react';
import {
  User,
  Calendar,
  Clock,
  ArrowUp,
  ArrowDown,
  Activity,
  FileText,
  AlertCircle,
  ChevronRight,
  Plus,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Save,
  Trash2,
  Edit,
  Trash,
  Eye,
  X,
  ZoomIn,
  ZoomOut,
  ChevronDown,
  Download,
  Paperclip,
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import AnalyticsExamples from '../components/AnalyticsExamples';
import ABLLSRDashboard from '../components/ABLLSRDashboard';
import PortageDashboard from '../components/PortageDashboard';
import SocialSkillsDashboard from '../components/SocialSkillsDashboard';
import VBMAPPDashboard from '../components/VBMAPPDashboard';
import { patients, sessions, treatmentPlans, behaviors, sessionNotes, users, insurance, getPatientObservations, triggerObservationAnalysis, assessments } from '../services/api';
import axios from 'axios';
import { isDemoMode, maskPatientName, getDemoModeIndicator, logDemoActivity } from '../utils/demoMode';
import TreatmentPlanTab from '../components/TreatmentPlanTab';
import { usePatientAccess } from '../utils/patientAccess';
import ExportPasswordDialog from '../components/ExportPasswordDialog';
import { parseScoreReport, validateScores, getClassification } from '../utils/scoreValidator';
import { parseLaudoAnalysis, validateLaudoAnalysis } from '../utils/laudoAnalysisValidator';
import LaudoStepper from '../components/LaudoStepper';
import AuditReportsTab from '../components/AuditReportsTab';

const currentHost = window.location.hostname;
const API_URL = currentHost === 'localhost'
  ? process.env.REACT_APP_API_URL
  : process.env.REACT_APP_API_URL_IP;

// Helper Components for Quality Modal
const QualityDomain = ({ title, domainKey, maxScore, criteria, scores, onScoreUpdate, domainTotals }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div 
        className="bg-gradient-to-r from-brand-600 to-brand-700 text-white p-4 cursor-pointer flex justify-between items-center"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3 className="font-semibold">{title}</h3>
        <div className="flex items-center gap-2">
          <span className="bg-white bg-opacity-20 px-3 py-1 rounded font-bold">
            {domainTotals[domainKey]}/{maxScore}
          </span>
          <ChevronRight 
            size={20} 
            className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`} 
          />
        </div>
      </div>
      
      {isExpanded && (
        <div className="p-4 bg-gray-50 space-y-4">
          {criteria.map((criterion) => (
            <div key={criterion.key} className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-3">
                <h4 className="font-medium text-gray-900">{criterion.title} ({criterion.maxPoints} pontos)</h4>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0"
                    max={criterion.maxPoints}
                    value={scores[criterion.key] || 0}
                    onChange={(e) => onScoreUpdate(criterion.key, e.target.value)}
                    className="w-32 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <span className="font-bold text-brand-600 min-w-[30px] text-center">
                    {scores[criterion.key] || 0}
                  </span>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 text-xs">
                {criterion.levels.map((level, idx) => {
                  const bgColors = ['bg-green-600', 'bg-teal-500', 'bg-yellow-500', 'bg-orange-500', 'bg-red-600'];
                  const textColors = ['text-white', 'text-white', 'text-gray-900', 'text-white', 'text-white'];
                  return (
                    <div key={idx} className={`${bgColors[idx]} ${textColors[idx]} p-2 rounded text-center font-medium`}>
                      {level}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const LLMQualityDomain = ({ title, domainKey, maxScore, reportData, criteria }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Extract domain score from LLM report
  const extractDomainScore = (reportData, domainKey) => {
    // First try to find a subtotal for this domain
    const subtotalPattern = new RegExp(`Subtotal\\s*Domínio\\s*${domainKey}[^\\d]*(\\d+)`, 'i');
    const subtotalMatch = reportData.match(subtotalPattern);
    if (subtotalMatch) {
      console.log(`[Domain ${domainKey}] Found subtotal:`, subtotalMatch[1]);
      return parseInt(subtotalMatch[1]);
    }
    
    // If no subtotal, calculate sum from individual criteria
    const criteriaKeys = {
      '1': ['1.1', '1.2'],
      '2': ['2.1', '2.2'], 
      '3': ['3.1', '3.2'],
      '4': ['4.1', '4.2'],
      '5': ['5.1', '5.2'],
      '6': ['6.1', '6.2']
    };
    
    const domainCriteria = criteriaKeys[domainKey] || [];
    let totalScore = 0;
    let foundScores = 0;
    
    console.log(`[Domain ${domainKey}] Looking for criteria:`, domainCriteria);
    
    for (const criterionKey of domainCriteria) {
      // Look for criterion scores in table format
      const patterns = [
        // Pattern with bold scores: "| 1.1 | ... | **7** |"
        new RegExp(`\\|\\s*${criterionKey}\\s*\\|[^\\|]*\\|[^\\|]*\\*\\*(\\d+)\\*\\*`, 'i'),
        // Pattern without bold: "| 1.1 | ... | 7 |"
        new RegExp(`\\|\\s*${criterionKey}\\s*\\|[^\\|]*\\|[^\\|]*\\|(\\d+)`, 'i'),
        // Simple pattern: "1.1 ... 7"
        new RegExp(`${criterionKey}[^\\d]*(\\d+)`, 'i')
      ];
      
      for (const pattern of patterns) {
        const match = reportData.match(pattern);
        if (match) {
          const score = parseInt(match[1]);
          totalScore += score;
          foundScores++;
          console.log(`[Domain ${domainKey}] Found ${criterionKey} score:`, score, 'Running total:', totalScore);
          break;
        }
      }
    }
    
    if (foundScores > 0) {
      console.log(`[Domain ${domainKey}] Calculated total from ${foundScores} criteria:`, totalScore);
      return totalScore;
    }
    
    console.log(`[Domain ${domainKey}] No subtotal or criteria scores found`);
    return 0;
  };

  // Extract criterion score and justification
  const extractCriterionData = (reportData, criterionKey) => {
    const patterns = [
      // Try to match table format with bold scores: "| 1.1 | ... | **7** | ... | justification"
      new RegExp(`\\|\\s*${criterionKey}\\s*\\|[^\\|]*\\|[^\\|]*\\*\\*(\\d+)\\*\\*[^\\|]*\\|[^\\|]*\\|\\s*([^\\|\\n]+)`, 'i'),
      // Try to match table format: "| 1.1 | Critérios Diagnósticos Reconhecidos | 7 | Adequado | Menciona TEA e características básicas..."
      new RegExp(`\\|\\s*${criterionKey}\\s*\\|[^\\|]*\\|\\s*(\\d+)\\s*\\|[^\\|]*\\|\\s*([^\\|\\n]+)`, 'i'),
      // Try to match simple format: "1.1 - 7 - Adequado"  
      new RegExp(`${criterionKey}[^\\d]*(\\d+)[^A-Za-z]*([A-Za-z][^\\n]*?)(?=\\n|$)`, 'i'),
      // Fallback pattern
      new RegExp(`${criterionKey}.*?(\\d+).*?([A-Za-z][^\\n]*?)(?=\\n|$)`, 'i')
    ];
    
    for (const pattern of patterns) {
      const match = reportData.match(pattern);
      if (match) {
        return {
          score: parseInt(match[1]) || 0,
          justification: match[2] ? match[2].trim() : 'Justificativa não encontrada'
        };
      }
    }
    return { score: 0, justification: 'Dados não encontrados no relatório' };
  };

  const domainScore = extractDomainScore(reportData, domainKey);
  
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div 
        className="bg-gradient-to-r from-brand-600 to-brand-700 text-white p-4 cursor-pointer flex justify-between items-center"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3 className="font-semibold">{title}</h3>
        <div className="flex items-center gap-2">
          <span className="bg-white bg-opacity-20 px-3 py-1 rounded font-bold">
            {domainScore}/{maxScore}
          </span>
          <ChevronRight 
            size={20} 
            className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`} 
          />
        </div>
      </div>
      
      {isExpanded && (
        <div className="p-4 bg-gray-50 space-y-4">
          {criteria.map((criterion) => {
            const criterionData = extractCriterionData(reportData, criterion.key);
            const getScoreLevel = (score, maxPoints) => {
              const percentage = (score / maxPoints) * 100;
              if (percentage >= 90) return { level: 'Excelente', color: 'bg-green-600 text-white' };
              if (percentage >= 70) return { level: 'Bom', color: 'bg-teal-500 text-white' };
              if (percentage >= 50) return { level: 'Adequado', color: 'bg-yellow-500 text-gray-900' };
              if (percentage >= 30) return { level: 'Fraco', color: 'bg-orange-500 text-white' };
              return { level: 'Inadequado', color: 'bg-red-600 text-white' };
            };
            
            const scoreLevel = getScoreLevel(criterionData.score, criterion.maxPoints);
            
            return (
              <div key={criterion.key} className="bg-white p-4 rounded-lg shadow-sm">
                <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 mb-3">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 mb-2">
                      {criterion.title} ({criterion.maxPoints} pontos)
                    </h4>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="text-2xl font-bold text-brand-600">
                        {criterionData.score}
                      </div>
                      <div className={`px-3 py-1 rounded-full text-sm font-medium ${scoreLevel.color}`}>
                        {scoreLevel.level}
                      </div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <h5 className="font-medium text-gray-800 mb-1">Justificativa:</h5>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        {criterionData.justification}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ReferenceSection = ({ title, items }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div 
        className="bg-brand-700 text-white p-4 cursor-pointer flex justify-between items-center"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3 className="font-semibold">{title}</h3>
        <ChevronRight 
          size={20} 
          className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`} 
        />
      </div>
      
      {isExpanded && (
        <div className="p-4 bg-gray-50 space-y-3">
          {items.map((item, idx) => (
            <div key={idx} className="bg-white p-4 rounded-lg border-l-4 border-brand-600">
              <h4 className="font-semibold text-gray-900 mb-2">{item.title}</h4>
              <a 
                href={item.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-brand-600 hover:text-brand-800 text-sm break-all"
              >
                {item.url}
              </a>
              <p className="text-gray-600 text-sm mt-2 leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CollapsibleSection = ({ title, defaultOpen = false, children }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <h3 className="text-base font-medium text-gray-900">{title}</h3>
        <ChevronDown
          size={18}
          className={`text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && <div className="p-4">{children}</div>}
    </div>
  );
};

// AvaliacoesTab Component

const AvaliacoesTab = ({ patientId, assessmentData, loading }) => {
  const [selectedAssessmentTab, setSelectedAssessmentTab] = useState('dashboard');
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [switchedParsedData, setSwitchedParsedData] = useState(null);
  const [switchedLoading, setSwitchedLoading] = useState(false);

  // Extract data from pre-loaded assessment data
  const defaultAssessment = assessmentData?.latestAssessment || null;
  const assessmentsList = assessmentData?.allAssessments || [];
  const error = !loading && !assessmentData ? 'Nenhuma avaliação encontrada' : null;

  // Group analyzed assessments by protocol to detect multi-protocol patients
  const analyzedByProtocol = {};
  assessmentsList.forEach(a => {
    if (a.extractedText && a.protocol?.name) {
      if (!analyzedByProtocol[a.protocol.name] || new Date(a.uploadTime) > new Date(analyzedByProtocol[a.protocol.name].uploadTime)) {
        analyzedByProtocol[a.protocol.name] = a;
      }
    }
  });
  const protocolNames = Object.keys(analyzedByProtocol);

  // Determine which assessment to show: user-selected protocol or default
  const activeProtocol = selectedProtocol || defaultAssessment?.protocol?.name || null;
  const latestAssessment = activeProtocol && analyzedByProtocol[activeProtocol]
    ? analyzedByProtocol[activeProtocol]
    : defaultAssessment;

  // Fetch parsed data when user switches to a different protocol
  const isDefaultProtocol = !selectedProtocol || selectedProtocol === defaultAssessment?.protocol?.name;
  const activeParsedData = isDefaultProtocol ? assessmentData?.parsedData : switchedParsedData;

  useEffect(() => {
    if (!selectedProtocol || selectedProtocol === defaultAssessment?.protocol?.name) {
      setSwitchedParsedData(null);
      return;
    }
    const targetAssessment = analyzedByProtocol[selectedProtocol];
    if (!targetAssessment) return;

    let cancelled = false;
    setSwitchedLoading(true);
    assessments.getParsedData(targetAssessment.id)
      .then(res => {
        if (!cancelled) {
          setSwitchedParsedData(res.data?.status === 'success' ? res.data.data : null);
        }
      })
      .catch(() => { if (!cancelled) setSwitchedParsedData(null); })
      .finally(() => { if (!cancelled) setSwitchedLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProtocol]);

  // Build the effective assessmentData for child dashboard components
  const effectiveAssessmentData = {
    ...assessmentData,
    latestAssessment: latestAssessment,
    parsedData: activeParsedData,
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-lg text-gray-600">Carregando avaliações...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="text-red-800">{error}</div>
      </div>
    );
  }

  if (!latestAssessment) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
        <div className="text-gray-600 mb-4">Nenhuma avaliação encontrada para este paciente.</div>
        <div className="text-sm text-gray-500">
          Faça upload de uma avaliação na página de edição do paciente.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Protocol Selector (when multiple protocols exist) */}
      {protocolNames.length > 1 && (
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-600">Protocolo:</span>
            <div className="flex gap-2">
              {protocolNames.map(name => (
                <button
                  key={name}
                  onClick={() => setSelectedProtocol(name)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${
                    activeProtocol === name
                      ? 'bg-brand-700 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Latest Assessment Overview */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Avaliação</h2>
          <span className="bg-brand-100 text-brand-800 text-sm font-medium px-3 py-1 rounded-full">
            {latestAssessment.protocol?.name || 'Protocolo Desconhecido'}
          </span>
        </div>

        <div className="flex items-center space-x-6 text-sm">
          <div className="flex items-center space-x-2">
            <span className="text-gray-600">Protocolo:</span>
            <span className="font-medium text-gray-900">
              {latestAssessment.protocol?.name || 'N/A'}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-gray-600">Data de Upload:</span>
            <span className="font-medium text-gray-900">
              {new Date(latestAssessment.uploadTime).toLocaleDateString('pt-BR')}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-gray-600">Versão:</span>
            <span className="font-medium text-gray-900">
              v{latestAssessment.version}
            </span>
          </div>
        </div>

        {latestAssessment.comments && (
          <div className="mt-4">
            <div className="text-sm text-gray-600 mb-1">Comentários</div>
            <div className="text-gray-900 bg-gray-50 p-3 rounded-lg">
              {latestAssessment.comments}
            </div>
          </div>
        )}
      </div>

      {/* Assessment Tabs */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="px-4 pt-4">
          <nav className="flex space-x-1">
            <button
              onClick={() => setSelectedAssessmentTab('dashboard')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-all duration-200 ${
                selectedAssessmentTab === 'dashboard'
                  ? 'bg-brand-700 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
              }`}
            >
              📊 Dashboard {latestAssessment.protocol?.name || ''}
            </button>
            <button
              onClick={() => setSelectedAssessmentTab('treatment')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-all duration-200 ${
                selectedAssessmentTab === 'treatment'
                  ? 'bg-brand-700 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
              }`}
            >
              📋 Plano de Tratamento
            </button>
          </nav>
        </div>

        <div className="p-6">
          {selectedAssessmentTab === 'dashboard' && (
            switchedLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="text-gray-600">Carregando dados do protocolo...</div>
              </div>
            ) : (
            <div className="space-y-4">
              {latestAssessment.protocol?.name === 'ABLLS-R' ? (
                <ABLLSRDashboard assessmentData={effectiveAssessmentData} />
              ) : latestAssessment.protocol?.name === 'Portage' ? (
                <PortageDashboard assessmentData={effectiveAssessmentData} />
              ) : latestAssessment.protocol?.name === 'Social Skills' ? (
                <SocialSkillsDashboard assessmentData={effectiveAssessmentData} />
              ) : latestAssessment.protocol?.name === 'VB-MAPP' ? (
                <VBMAPPDashboard assessmentData={effectiveAssessmentData} />
              ) : (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Dashboard {latestAssessment.protocol?.name || ''}
                  </h3>
                  {latestAssessment.extractedText ? (
                    <div className="bg-brand-50 border border-brand-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-brand-600 mr-2" />
                        <span className="text-brand-800 font-medium">Avaliação Analisada</span>
                      </div>
                      <div className="text-brand-700 text-sm mt-1">
                        Dashboard específico para {latestAssessment.protocol?.name || ''} será implementado em breve.
                      </div>
                    </div>
                  ) : (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
                        <span className="text-yellow-800 font-medium">Aguardando Análise</span>
                      </div>
                      <div className="text-yellow-700 text-sm mt-1">
                        A avaliação precisa ser analisada antes de exibir o dashboard.
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            )
          )}

          {selectedAssessmentTab === 'treatment' && (
            <TreatmentPlanTab
              patientId={patientId}
              assessmentData={latestAssessment}
            />
          )}
        </div>
      </div>

    </div>
  );
};

const PatientDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [assessmentDashboardData, setAssessmentDashboardData] = useState(null);
  const [assessmentDashboardLoading, setAssessmentDashboardLoading] = useState(false);
  const [sessionNotes, setSessionNotes] = useState({
    whatWentRight: '',
    whatWentWrong: '',
    alerts: '',
    nextSteps: '',
    parentFeedback: ''
  });
  const [selectedSession, setSelectedSession] = useState(null);
  const [newSession, setNewSession] = useState({
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    duration: 60,
    sessionType: 'residence',
    location: '',
    status: 'completed',
    checkInTime: null,
    checkOutTime: null,
    notes: {
      whatWentRight: '',
      whatWentWrong: '',
      alerts: '',
      nextSteps: '',
      parentFeedback: ''
    }
  });
  const [showNewSessionForm, setShowNewSessionForm] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  
  // Use patient access utility
  const { redirectIfNoAccess } = usePatientAccess();
  const [patientSessions, setPatientSessions] = useState([]);
  const [treatmentPlan, setTreatmentPlan] = useState(null);
  const [behaviorData, setBehaviorData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [therapist, setTherapist] = useState(null);
  const [insuranceProvider, setInsuranceProvider] = useState(null);
  
  // Preview states
  const [showPreview, setShowPreview] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [observations, setObservations] = useState([]);
  const [obsLoading, setObsLoading] = useState(false);
  const [obsError, setObsError] = useState(null);
  const [obsStartDate, setObsStartDate] = useState(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [obsEndDate, setObsEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [obsSpecialtyFilter, setObsSpecialtyFilter] = useState(null);
  const [analyticsTab, setAnalyticsTab] = useState('dominio');
  const [obsAnalysis, setObsAnalysis] = useState('');
  const [obsAnalysisLoading, setObsAnalysisLoading] = useState(false);
  const [obsAnalysisError, setObsAnalysisError] = useState(null);
  // Laudo export state
  const [exportingScoreReport, setExportingScoreReport] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  // Add state for triggering analysis
  const [triggerAnalysisLoading, setTriggerAnalysisLoading] = useState(false);
  const [triggerAnalysisError, setTriggerAnalysisError] = useState(null);
  // Add state for assessment tabs
  const [assessmentActiveTab, setAssessmentActiveTab] = useState('assessment');

  
  // Fetch patient data, sessions, treatment plan, and behaviors
  const fetchPatientData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if user has access to this patient
      const hasAccess = await redirectIfNoAccess(id, navigate);
      if (!hasAccess) {
        return; // redirectIfNoAccess handles the redirect
      }

      console.log('Fetching patient data for ID:', id);
      const patientResponse = await patients.getById(id);
      console.log('Patient API response:', patientResponse);

      if (!patientResponse.data) {
        throw new Error('No data received from patient API');
      }

      const patientData = patientResponse.data;
      console.log('Patient data:', patientData);

      // Set patient data
      setPatient(patientData);

      // Set therapist from nested object
      if (patientData.primaryTherapistUser) {
        console.log('Setting therapist:', patientData.primaryTherapistUser);
        setTherapist(patientData.primaryTherapistUser);
      } else {
        console.log('No primary therapist user data found');
        setTherapist(null);
      }

      // Set insurance provider from nested object
      if (patientData.insuranceCompany) {
        console.log('Setting insurance provider:', patientData.insuranceCompany);
        setInsuranceProvider(patientData.insuranceCompany);
      } else {
        console.log('No insurance company data found');
        setInsuranceProvider(null);
      }

      // Set sessions from nested array
      if (Array.isArray(patientData.patientSessions)) {
        console.log('Setting patient sessions:', patientData.patientSessions);
        setPatientSessions(patientData.patientSessions);
      } else {
        console.log('No patient sessions found');
        setPatientSessions([]);
      }

      // Set behaviors from nested array
      if (Array.isArray(patientData.behaviors)) {
        console.log('Setting behaviors:', patientData.behaviors);
        setBehaviorData(patientData.behaviors);
      } else {
        console.log('No behaviors found');
        setBehaviorData([]);
      }

      // Fetch treatment plan separately if not included in patient data
      try {
        if (treatmentPlans && treatmentPlans.getByPatient) {
          const treatmentPlanResponse = await treatmentPlans.getByPatient(id);
          console.log('Treatment plan response:', treatmentPlanResponse);
          if (treatmentPlanResponse.data) {
            setTreatmentPlan(treatmentPlanResponse.data);
          }
        } else {
          console.log('Treatment plan API not available, skipping');
        }
      } catch (error) {
        console.error('Error fetching treatment plan:', error);
        // Don't throw here, just log the error
      }

      // Fetch assessments and load dashboard data
      try {
        setAssessmentDashboardLoading(true);
        const assessmentsResponse = await assessments.getByPatient(id);
        const assessmentsData = assessmentsResponse.data?.status === 'success' ? assessmentsResponse.data.data : assessmentsResponse.data;
        
        if (assessmentsData && assessmentsData.length > 0) {
          // Sort by upload time descending
          const sorted = assessmentsData.sort((a, b) => new Date(b.uploadTime) - new Date(a.uploadTime));
          // Pick the latest uploaded assessment that has been analyzed; fall back to latest overall
          const latestAnalyzed = sorted.find(a => a.extractedText && a.protocol?.name);
          const latestAssessment = latestAnalyzed || sorted[0];

          // Try to load the dashboard data if assessment is analyzed
          if (latestAssessment.extractedText && latestAssessment.protocol?.name) {
            try {
              console.log('Attempting to fetch parsed data for assessment:', latestAssessment.id);
              const parsedResponse = await assessments.getParsedData(latestAssessment.id);
              
              if (parsedResponse.data?.status === 'success') {
                setAssessmentDashboardData({
                  latestAssessment,
                  allAssessments: assessmentsData,
                  parsedData: parsedResponse.data.data
                });
                console.log('Assessment dashboard data loaded successfully');
              } else {
                console.log('Parsed assessment data not found, setting basic assessment data');
                setAssessmentDashboardData({
                  latestAssessment,
                  allAssessments: assessmentsData,
                  parsedData: null
                });
              }
            } catch (parseError) {
              console.error('Error loading parsed assessment data:', parseError);
              setAssessmentDashboardData({
                latestAssessment,
                allAssessments: assessmentsData,
                parsedData: null
              });
            }
          } else {
            console.log('Assessment not analyzed yet, setting basic assessment data');
            setAssessmentDashboardData({
              latestAssessment,
              allAssessments: assessmentsData,
              parsedData: null
            });
          }
        } else {
          setAssessmentDashboardData(null);
        }
      } catch (error) {
        console.error('Error fetching assessments:', error);
        setAssessmentDashboardData(null);
        // Don't throw here, just log the error
      } finally {
        setAssessmentDashboardLoading(false);
      }

    } catch (error) {
      console.error('Error in fetchPatientData:', error);
      setError(error.message || 'Failed to load patient data');
      // Clear data on error
      setPatient(null);
      setTherapist(null);
      setInsuranceProvider(null);
      setPatientSessions([]);
      setBehaviorData([]);
      setTreatmentPlan(null);
    } finally {
      setLoading(false);
    }
  };

  // Update useEffect to handle errors
  useEffect(() => {
    if (id) {
      console.log('PatientDashboard mounted/updated with ID:', id);
      fetchPatientData().catch(error => {
        console.error('Error in useEffect:', error);
      });
    } else {
      console.error('No patient ID provided');
      setError('No patient ID provided');
    }
  }, [id]);

  useEffect(() => {
    if (activeTab === 'observacao') {
      setObsLoading(true);
      setObsError(null);
      getPatientObservations(id, obsStartDate, obsEndDate)
        .then(res => {
          if (res.data?.status === 'success') {
            setObservations(res.data.data);
          } else {
            setObservations([]);
            setObsError(res.data?.message || 'Erro ao carregar observações');
          }
        })
        .catch(err => setObsError(err.message || 'Erro ao carregar observações'))
        .finally(() => setObsLoading(false));
    }
  }, [activeTab, obsStartDate, obsEndDate, id]);



  // Handle tab click
  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  // Sample progress metrics
  const progressMetrics = [
    { name: 'Comunicação', current: 72, previous: 65, target: 90 },
    { name: 'Habilidades Sociais', current: 58, previous: 50, target: 85 },
    { name: 'Autocuidado', current: 80, previous: 75, target: 95 },
    { name: 'Acadêmico', current: 65, previous: 55, target: 80 }
  ];
  
  // Sample upcoming sessions
  const upcomingSessions = [
    { id: 1, date: 'Mar 15, 2025', time: '10:30 AM', type: 'Terapia ABA', duration: '1,5 horas', therapist: 'Dr. Jamie Smith' },
    { id: 2, date: 'Mar 17, 2025', time: '2:00 PM', type: 'Fonoaudiologia', duration: '1 hora', therapist: 'Lisa Chen, Fonoaudióloga' },
    { id: 3, date: 'Mar 19, 2025', time: '11:00 AM', type: 'Terapia ABA', duration: '1,5 horas', therapist: 'Dr. Jamie Smith' }
  ];
  
  // Sample recent activities
  const recentActivities = [
    { id: 1, date: 'Mar 12, 2025', time: '11:23 AM', type: 'Session Note', title: 'Sessão de Terapia ABA', user: 'Dr. Jamie Smith', highlight: 'Ótimo progresso com atividades de revezamento' },
    { id: 2, date: 'Mar 10, 2025', time: '3:45 PM', type: 'Parent Note', title: 'Observação do Fim de Semana', user: 'Robin Johnson', highlight: 'Dificuldades no supermercado' },
    { id: 3, date: 'Mar 8, 2025', time: '10:15 AM', type: 'Assessment', title: 'Avaliação Mensal de Progresso', user: 'Dr. Jamie Smith', highlight: 'Melhora em todos os domínios' },
    { id: 4, date: 'Mar 5, 2025', time: '2:30 PM', type: 'Message', title: 'Coordenação Escolar', user: 'Teacher Adams', highlight: 'Solicitando contribuições para reunião do PEI' }
  ];
  
  // Sample alerts
  const alerts = [
    { id: 1, type: 'high', message: 'Autorização do convênio expira em 10 dias', date: 'Mar 22, 2025' },
    { id: 2, type: 'medium', message: 'Responsável solicitou atualização sobre metas de comunicação', date: 'Mar 10, 2025' }
  ];

  // Sample behavior data
  const behaviors = [
    {
      id: 1,
      name: 'Comunicação Verbal',
      frequency: 'Diário',
      severity: 'Médio',
      triggers: ['Situações sociais', 'Transições'],
      interventions: ['Agenda visual', 'Sistema de fichas'],
      lastIncident: '2025-03-12',
      status: 'Melhorando'
    },
    {
      id: 2,
      name: 'Comportamento Auto-Lesivo',
      frequency: 'Semanal',
      severity: 'Alto',
      triggers: ['Sobrecarga sensorial', 'Frustração'],
      interventions: ['Pausas sensoriais', 'Técnicas de desescalada'],
      lastIncident: '2025-03-10',
      status: 'Estável'
    },
    {
      id: 3,
      name: 'Fuga',
      frequency: 'Ocasional',
      severity: 'Alto',
      triggers: ['Ambientes lotados', 'Barulhos altos'],
      interventions: ['Protocolo de segurança', 'Limites visuais'],
      lastIncident: '2025-03-08',
      status: 'Melhorando'
    }
  ];

  // Sample historical sessions with notes
  const historicalSessions = [
    {
      id: 1,
      date: '12 Mar 2025',
      time: '10:30',
      type: 'Terapia ABA',
      duration: '1,5 horas',
      therapist: 'Dr. Jamie Smith',
      notes: 'Ótimo progresso com atividades de revezamento. Paciente demonstrou melhor contato visual e engajamento.',
      goals: ['Comunicação', 'Habilidades Sociais'],
      status: 'Concluído'
    },
    {
      id: 2,
      date: '10 Mar 2025',
      time: '14:00',
      type: 'Fonoaudiologia',
      duration: '1 hora',
      therapist: 'Lisa Chen',
      notes: 'Trabalhou em exercícios de articulação. Paciente demonstrou bom foco e esforço.',
      goals: ['Comunicação', 'Desenvolvimento da Linguagem'],
      status: 'Concluído'
    },
    {
      id: 3,
      date: '8 Mar 2025',
      time: '11:00',
      type: 'Terapia ABA',
      duration: '1,5 horas',
      therapist: 'Dr. Jamie Smith',
      notes: 'Foco em habilidades de interação social. Paciente demonstrou melhora em iniciar conversas.',
      goals: ['Habilidades Sociais', 'Comunicação'],
      status: 'Concluído'
    }
  ];

  // Handle new session creation
  const handleCreateSession = async (e) => {
    e.preventDefault();
    try {
      const sessionData = {
        ...newSession,
        patientId: id,
        type: 'ABA Therapy',
        notes: JSON.stringify(newSession.notes)
      };
      
      console.log('Creating session with data:', sessionData);
      const response = await sessions.create(sessionData);
      console.log('New session created:', response.data);
      
      // Fetch updated patient data to get the new session
      const patientResponse = await patients.getById(id);
      console.log('Updated patient data:', patientResponse);
      
      if (patientResponse.data && patientResponse.data.patientSessions) {
        setPatientSessions(patientResponse.data.patientSessions);
      }
      
      // Reset form and close modal
      setNewSession({
        date: new Date().toISOString().split('T')[0],
        time: new Date().toTimeString().slice(0, 5),
        duration: 60,
        sessionType: 'residence',
        location: '',
        status: 'completed',
        checkInTime: null,
        checkOutTime: null,
        notes: {
          whatWentRight: '',
          whatWentWrong: '',
          alerts: '',
          nextSteps: '',
          parentFeedback: ''
        }
      });
      setShowNewSessionForm(false);
      setError(null);
    } catch (error) {
      console.error('Error creating session:', error);
      setError('Failed to create session. Please try again.');
    }
  };

  // Handle session note submission
  const handleSubmitNote = async (e) => {
    e.preventDefault();
    if (selectedSession) {
      try {
        // Update the session with the new notes
        const sessionData = {
          ...selectedSession,
          notes: JSON.stringify(sessionNotes) // sessionNotes is now an object
        };

        console.log('Updating session with notes:', sessionData);
        const response = await sessions.update(selectedSession.id, sessionData);
        console.log('Session updated:', response.data);

        // Update the session in the list
        const updatedSessions = patientSessions.map(session => {
          if (session.id === selectedSession.id) {
            return { ...session, notes: JSON.stringify(sessionNotes) };
          }
          return session;
        });
        setPatientSessions(updatedSessions);
        
        // Reset form
        setSessionNotes({
          whatWentRight: '',
          whatWentWrong: '',
          alerts: '',
          nextSteps: '',
          parentFeedback: ''
        });
        setSelectedSession(null);
        setError(null);
      } catch (error) {
        console.error('Error saving note:', error);
        setError('Failed to save note. Please try again.');
      }
    }
  };

  const calculateAge = (dateOfBirth) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };


  // Export score report handler - shows password dialog first
  const handleExportScoreReport = () => {
    setShowExportDialog(true);
  };

  const doExportScoreReport = async (password) => {
    setShowExportDialog(false);
    setExportingScoreReport(true);
    try {
      const extraHeaders = {};
      if (password) {
        extraHeaders['X-Export-Password'] = btoa(password);
      }

      const response = await patients.exportScoreReport(id, extraHeaders);
      // Create blob from response
      const blob = new Blob([response.data], {
        type: response.isEncrypted
          ? 'application/zip'
          : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.filename || `Laudo_Completo_${patient?.firstName}_${patient?.lastName}_${Date.now()}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Erro ao exportar relatório: ' + (err?.message || err));
    } finally {
      setExportingScoreReport(false);
    }
  };

  // Update the parseSessionNotes function
  const parseSessionNotes = (notes) => {
    if (!notes) {
      return {
        whatWentRight: '',
        whatWentWrong: '',
        alerts: '',
        nextSteps: '',
        parentFeedback: ''
      };
    }

    // If notes is already an object, return it with default values for missing fields
    if (typeof notes === 'object' && !Array.isArray(notes)) {
      return {
        whatWentRight: notes.whatWentRight || '',
        whatWentWrong: notes.whatWentWrong || '',
        alerts: notes.alerts || '',
        nextSteps: notes.nextSteps || '',
        parentFeedback: notes.parentFeedback || ''
      };
    }

    try {
      // Try to parse as JSON if it's a string
      const parsedNotes = JSON.parse(notes);
      if (typeof parsedNotes === 'object' && !Array.isArray(parsedNotes)) {
        return {
          whatWentRight: parsedNotes.whatWentRight || '',
          whatWentWrong: parsedNotes.whatWentWrong || '',
          alerts: parsedNotes.alerts || '',
          nextSteps: parsedNotes.nextSteps || '',
          parentFeedback: parsedNotes.parentFeedback || ''
        };
      }
    } catch (error) {
      // If parsing fails, treat the entire notes as whatWentRight
      console.log('Error parsing notes:', error);
    }

    // Default case: treat the entire notes as whatWentRight
    return {
      whatWentRight: String(notes) || '',
      whatWentWrong: '',
      alerts: '',
      nextSteps: '',
      parentFeedback: ''
    };
  };


  // Helper to format date in Brazilian Portuguese
  function formatDateBR(dateStr) {
    if (!dateStr) return '';
    
    // Handle different date formats
    let date;
    if (dateStr.includes('Mar ') || dateStr.includes('Jan ') || dateStr.includes('Feb ') || 
        dateStr.includes('Apr ') || dateStr.includes('May ') || dateStr.includes('Jun ') ||
        dateStr.includes('Jul ') || dateStr.includes('Aug ') || dateStr.includes('Sep ') ||
        dateStr.includes('Oct ') || dateStr.includes('Nov ') || dateStr.includes('Dec ')) {
      // Handle English format like "Mar 15, 2025"
      date = new Date(dateStr);
    } else if (dateStr.includes('-')) {
      // Handle YYYY-MM-DD format
      const [year, month, day] = dateStr.split('-');
      date = new Date(year, month - 1, day);
    } else {
      date = new Date(dateStr);
    }
    
    if (isNaN(date.getTime())) return dateStr;
    
    const months = [
      'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
      'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
    ];
    
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    
    return `${day} de ${month} de ${year}`;
  }

  // Helper to format time to 24-hour Brazilian format
  function formatTimeBR(timeStr) {
    if (!timeStr) return '';
    
    if (timeStr.includes('AM') || timeStr.includes('PM')) {
      // Convert from AM/PM to 24-hour format
      const [time, period] = timeStr.split(' ');
      const [hours, minutes] = time.split(':');
      let hour24 = parseInt(hours);
      
      if (period === 'PM' && hour24 !== 12) {
        hour24 += 12;
      } else if (period === 'AM' && hour24 === 12) {
        hour24 = 0;
      }
      
      return `${hour24.toString().padStart(2, '0')}:${minutes}`;
    }
    
    return timeStr;
  }

  // Helper to get file type and check if it's previewable
  const getFileType = (filename) => {
    if (!filename) return null;
    const ext = filename.toLowerCase().split('.').pop();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
      return 'image';
    } else if (ext === 'pdf') {
      return 'pdf';
    }
    return 'unknown';
  };

  // Authenticated file download handler
  const [laudoFileUnavailable, setLaudoFileUnavailable] = useState(false);

  // Check if the laudo file actually exists on the server (HEAD-style check via JSON endpoint)
  const checkLaudoAvailability = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/patients/${patient.id}/laudo/download?view=true`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          validateStatus: () => true // Accept any status
        }
      );
      // Detect errors: JSON error body, HTML (CloudFront SPA fallback for 404), or non-200
      const contentType = response.headers['content-type'] || '';
      const isErrorResponse = response.status !== 200 ||
        contentType.includes('application/json') ||
        contentType.includes('text/html');

      if (isErrorResponse || response.data.size < 1024) {
        // Double-check small blobs: read as text to see if it's an error
        if (response.data.size < 1024 && !isErrorResponse) {
          try {
            const text = await response.data.text();
            const parsed = JSON.parse(text);
            if (parsed.status === 'error') {
              setLaudoFileUnavailable(true);
              return false;
            }
          } catch {
            // Not JSON, might be a legitimately small file — allow it
            return true;
          }
        }
        if (isErrorResponse) {
          setLaudoFileUnavailable(true);
          return false;
        }
      }
      return true;
    } catch {
      setLaudoFileUnavailable(true);
      return false;
    }
  };

  const downloadLaudoFile = async (openInNewTab = false) => {
    try {
      const token = localStorage.getItem('token');

      // Add view=true parameter when opening in new tab
      const url = openInNewTab
        ? `${API_URL}/api/patients/${patient.id}/laudo/download?view=true`
        : `${API_URL}/api/patients/${patient.id}/laudo/download`;

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        responseType: 'blob',
        validateStatus: () => true // Accept any status
      });

      // Check response status before creating blob
      if (response.status !== 200) {
        setLaudoFileUnavailable(true);
        setShowPreview(false);
        return;
      }

      // Get content type from response headers
      const contentType = response.headers['content-type'] || '';

      // Verify we got a real file, not a JSON error or CloudFront SPA fallback (text/html)
      if (contentType.includes('application/json') || contentType.includes('text/html')) {
        setLaudoFileUnavailable(true);
        setShowPreview(false);
        return;
      }

      // Extra safety: check if blob is suspiciously small (likely an error response)
      if (response.data.size < 1024) {
        try {
          const text = await response.data.text();
          const parsed = JSON.parse(text);
          if (parsed.status === 'error') {
            setLaudoFileUnavailable(true);
            setShowPreview(false);
            return;
          }
        } catch {
          // Not JSON — proceed normally
        }
      }

      // Create a blob with the correct MIME type
      const blob = new Blob([response.data], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);

      if (openInNewTab) {
        // Open in new tab
        window.open(blobUrl, '_blank');
        // Clean up the blob URL after a delay to allow the tab to open
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
      } else {
        // Download the file
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = patient.laudoFile || 'laudo.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the blob URL after a short delay
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
      }
    } catch (error) {
      console.error('Error downloading laudo:', error);
      setLaudoFileUnavailable(true);
      setShowPreview(false);
    }
  };

  // Preview Modal Component
  const PreviewModal = ({ filename, onClose }) => {
    const fileType = getFileType(filename);

    if (!showPreview) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg max-w-6xl max-h-[90vh] w-full mx-4 flex flex-col">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-medium text-gray-900">Pré-visualização do Laudo</h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={onClose}
                className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md"
              >
                <X size={20} />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-auto p-4">
            {fileType === 'image' && (
              <div className="text-center space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                    <Eye className="text-green-600" size={24} />
                  </div>
                  <h4 className="text-lg font-medium text-green-900 mb-2">Imagem Detectada</h4>
                  <p className="text-green-700 text-sm mb-4">
                    Para visualizar a imagem completa, clique no botão abaixo para abrir em uma nova aba.
                  </p>
                  <button
                    onClick={() => downloadLaudoFile(true)}
                    className="px-6 py-3 bg-brand-700 text-white rounded-lg font-medium hover:bg-brand-800 transition-colors flex items-center space-x-2 mx-auto"
                  >
                    <Eye size={20} />
                    <span>Abrir Imagem em Nova Aba</span>
                  </button>
                </div>
                <p className="text-gray-500 text-sm">
                  <strong>Arquivo:</strong> {filename}
                </p>
                <div className="text-xs text-gray-400 bg-gray-50 p-3 rounded">
                  <p><strong>Nota:</strong> A pré-visualização inline foi desabilitada devido às configurações de segurança CORS do servidor.</p>
                </div>
              </div>
            )}
            {fileType === 'pdf' && (
              <div className="text-center space-y-4">
                <div className="bg-brand-50 border border-brand-200 rounded-lg p-6">
                  <FileText className="mx-auto text-brand-600 mb-4" size={48} />
                  <h4 className="text-lg font-medium text-brand-900 mb-2">Documento PDF Detectado</h4>
                  <p className="text-brand-700 text-sm mb-4">
                    Para visualizar o PDF completo, clique no botão abaixo para abrir em uma nova aba.
                  </p>
                  <button
                    onClick={() => downloadLaudoFile(true)}
                    className="px-6 py-3 bg-brand-700 text-white rounded-lg font-medium hover:bg-brand-800 transition-colors flex items-center space-x-2 mx-auto"
                  >
                    <Eye size={20} />
                    <span>Abrir PDF em Nova Aba</span>
                  </button>
                </div>
                <p className="text-gray-500 text-sm">
                  <strong>Arquivo:</strong> {filename}
                </p>
              </div>
            )}
            {fileType === 'unknown' && (
              <div className="text-center text-gray-500 py-12">
                <FileText size={48} className="mx-auto mb-4 text-gray-400" />
                <p>Pré-visualização não disponível para este tipo de arquivo.</p>
                <p className="text-sm mt-2">Tipos suportados: PDF, JPG, PNG, GIF, WEBP</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading patient data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <AlertCircle size={48} className="mx-auto" />
          </div>
          <p className="text-xl text-gray-600 mb-4">Error loading patient data</p>
          <p className="text-gray-500 mb-4">{error}</p>
          <button 
            onClick={() => {
              setError(null);
              fetchPatientData();
            }}
            className="px-4 py-2 bg-brand-700 text-white rounded-md hover:bg-brand-800 mr-2"
          >
            Try Again
          </button>
          <button 
            onClick={() => navigate('/dashboard/patients')}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
          >
            Return to Patients List
          </button>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-xl text-gray-600 mb-4">Patient not found</p>
          <button 
            onClick={() => navigate('/dashboard/patients')}
            className="px-4 py-2 bg-brand-700 text-white rounded-md hover:bg-brand-800"
          >
            Return to Patients List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Patient Header */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex flex-col md:flex-row justify-between">
          <div className="flex items-center mb-4 md:mb-0">
            <div className="w-16 h-16 rounded-full bg-brand-100 flex items-center justify-center text-brand-600 mr-4">
              <User size={28} />
            </div>
            <div>
              <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">
                  {maskPatientName(`${patient.firstName} ${patient.lastName}`, patient.id)}
              </h1>
              <button
                onClick={() => navigate(`/dashboard/patients/${patient.id}/edit`)}
                className="p-1.5 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-md transition-colors duration-200"
                title="Editar paciente"
              >
                <Edit size={18} />
              </button>
                {getDemoModeIndicator() && (
                  <span className={getDemoModeIndicator().className}>
                    {getDemoModeIndicator().icon} {getDemoModeIndicator().text}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center text-sm text-gray-600">
                <span className="mr-3">{calculateAge(patient.dateOfBirth)} anos</span>
                <span className="mr-3">Data de Nascimento: {formatDateBR(patient.dateOfBirth)}</span>
                <span className="mr-3">ID: {patient.patientId}</span>
                {patient.primaryDiagnosis && (
                  <span className="bg-brand-100 text-brand-800 py-1 px-2 rounded-full text-xs font-medium">
                    {patient.primaryDiagnosis}
                  </span>
                )}
              </div>
              <div className="mt-2 text-sm text-gray-600">
                <p><strong>Terapeuta Principal:</strong> {therapist ? `${therapist.firstName} ${therapist.lastName}` : 'Não atribuído'}</p>
                <p><strong>Convênio:</strong> {insuranceProvider ? insuranceProvider.name : 'Não atribuído'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          className={`py-3 px-6 ${activeTab === 'overview' ? 'border-b-2 border-brand-600 text-brand-600 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
          onClick={() => handleTabClick('overview')}
        >
          Visão Geral
        </button>
        <button
          className={`py-3 px-6 ${activeTab === 'sessions' ? 'border-b-2 border-brand-600 text-brand-600 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
          onClick={() => handleTabClick('sessions')}
        >
          Sessões
        </button>
        <button
          className={`py-3 px-6 ${activeTab === 'behaviors' ? 'border-b-2 border-brand-600 text-brand-600 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
          onClick={() => handleTabClick('behaviors')}
        >
          Comportamentos
        </button>
        <button
          className={`py-3 px-6 ${activeTab === 'observacao' ? 'border-b-2 border-brand-600 text-brand-600 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
          onClick={() => handleTabClick('observacao')}
        >
          Observação
        </button>
        <button
          className={`py-3 px-6 ${activeTab === 'analytics' ? 'border-b-2 border-brand-600 text-brand-600 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
          onClick={() => handleTabClick('analytics')}
        >
          Análises
        </button>
        <button
          className={`py-3 px-6 ${activeTab === 'laudo' ? 'border-b-2 border-brand-600 text-brand-600 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
          onClick={() => handleTabClick('laudo')}
        >
          Laudo
        </button>
        <button
          className={`py-3 px-6 ${activeTab === 'avaliacoes' ? 'border-b-2 border-brand-600 text-brand-600 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
          onClick={() => handleTabClick('avaliacoes')}
        >
          Avaliações
        </button>
        <button
          className={`py-3 px-6 ${activeTab === 'auditReports' ? 'border-b-2 border-brand-600 text-brand-600 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
          onClick={() => handleTabClick('auditReports')}
        >
          Relatórios de Auditoria
        </button>
      </div>
      
      {/* Main Content - Overview */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* First Column */}
          <div className="col-span-2 space-y-6">
            {/* Progress Overview */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-medium text-gray-900">Resumo do Progresso</h2>
                <button className="text-brand-600 text-sm font-medium">Ver Todas as Metas</button>
              </div>
              
              <div className="space-y-4">
                {progressMetrics.map((metric) => (
                  <div key={metric.name}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-gray-700">{metric.name}</span>
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-900 mr-2">{metric.current}%</span>
                        <span className={`text-xs ${metric.current > metric.previous ? 'text-green-600' : 'text-red-600'}`}>
                          {metric.current > metric.previous ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                          {Math.abs(metric.current - metric.previous)}%
                        </span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-brand-700 h-2 rounded-full" 
                        style={{ width: `${metric.current}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Atividades Recentes</h2>
              <div className="space-y-4">
                {recentActivities.map((activity) => (
                  <div key={activity.id} className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center">
                        <FileText size={16} className="text-brand-600" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-sm font-medium text-gray-900">{activity.title}</h3>
                          <p className="text-sm text-gray-500">{activity.highlight}</p>
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatDateBR(activity.date)} {activity.time}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Second Column */}
          <div className="space-y-6">
            {/* Alerts */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Alertas</h2>
              <div className="space-y-4">
                {alerts.map((alert) => (
                  <div key={alert.id} className="flex items-start space-x-3">
                    <AlertCircle 
                      size={20} 
                      className={`${
                        alert.type === 'high' ? 'text-red-500' : 
                        alert.type === 'medium' ? 'text-yellow-500' : 
                        'text-blue-500'
                      }`}
                    />
                    <div>
                      <p className="text-sm text-gray-900">{alert.message}</p>
                      <p className="text-xs text-gray-500">{formatDateBR(alert.date)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Upcoming Sessions */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Próximas Sessões</h2>
              <div className="space-y-4">
                {upcomingSessions.map((session) => (
                  <div key={session.id} className="flex items-start space-x-3">
                    <Calendar size={20} className="text-brand-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{session.type || 'Sessão'}</p>
                      <p className="text-xs text-gray-500">{formatDateBR(session.date)} às {formatTimeBR(session.time)}</p>
                      <p className="text-xs text-gray-500">
                        {session.duration && session.therapist 
                          ? `${session.duration} com ${session.therapist}`
                          : session.duration 
                          ? session.duration
                          : session.therapist 
                          ? `com ${session.therapist}`
                          : 'Detalhes não disponíveis'
                        }
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content - Sessions */}
      {activeTab === 'sessions' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Sessions List */}
          <div className="col-span-2 space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-medium text-gray-900">Histórico de Sessões</h2>
                <button 
                  onClick={() => setShowNewSessionForm(true)}
                  className="px-3 py-2 bg-brand-700 text-white rounded-lg text-sm font-medium flex items-center"
                >
                  <Plus size={16} className="mr-1" />
                  Nova Sessão
                </button>
              </div>
              
              {error && (
                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
                  {error}
                </div>
              )}
              
              <div className="space-y-4">
                {patientSessions.map((session) => (
                  <div 
                    key={session.id} 
                    className={`p-4 rounded-lg border ${
                      selectedSession?.id === session.id ? 'border-brand-500 bg-brand-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">{session.type}</h3>
                        <p className="text-sm text-gray-500">{formatDateBR(session.date)} às {formatTimeBR(session.time)}</p>
                        <p className="text-sm text-gray-500">{session.duration} minutos</p>
                      </div>
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => {
                            console.log('Selected session:', session);
                            setSelectedSession(session);
                            const parsedNotes = parseSessionNotes(session.notes);
                            console.log('Parsed notes:', parsedNotes);
                            setSessionNotes(parsedNotes);
                          }}
                          className="text-brand-600 hover:text-brand-700"
                        >
                          <FileText size={16} />
                        </button>
                        <button className="text-gray-400 hover:text-gray-500">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Session Notes Form */}
          <div className="space-y-6">
            {showNewSessionForm ? (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Nova Sessão</h2>
                <form onSubmit={handleCreateSession} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data
                    </label>
                    <input
                      type="date"
                      value={newSession.date}
                      onChange={(e) => setNewSession({ ...newSession, date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Horário
                    </label>
                    <input
                      type="time"
                      value={newSession.time}
                      onChange={(e) => setNewSession({ ...newSession, time: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Duração (minutos)
                    </label>
                    <input
                      type="number"
                      value={newSession.duration}
                      onChange={(e) => setNewSession({ ...newSession, duration: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo de Sessão
                    </label>
                    <select
                      value={newSession.sessionType}
                      onChange={(e) => setNewSession({ ...newSession, sessionType: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                    >
                      <option value="residence">At Patient's Residence</option>
                      <option value="video">Video Conference</option>
                    </select>
                  </div>
                  
                  {newSession.sessionType === 'residence' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Detalhes do Local
                      </label>
                      <input
                        type="text"
                        value={newSession.location}
                        onChange={(e) => setNewSession({ ...newSession, location: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                        placeholder="Enter additional location details if needed"
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Horário de Entrada
                      </label>
                      <div className="px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-500">
                        {newSession.checkInTime || 'Não registrado'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Horário de Saída
                      </label>
                      <div className="px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-500">
                        {newSession.checkOutTime || 'Não registrado'}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        O que deu certo
                      </label>
                      <textarea
                        value={newSession.notes.whatWentRight}
                        onChange={(e) => setNewSession({
                          ...newSession,
                          notes: { ...newSession.notes, whatWentRight: e.target.value }
                        })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                        placeholder="Descreva sucessos e conquistas..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        O que deu errado
                      </label>
                      <textarea
                        value={newSession.notes.whatWentWrong}
                        onChange={(e) => setNewSession({
                          ...newSession,
                          notes: { ...newSession.notes, whatWentWrong: e.target.value }
                        })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                        placeholder="Descreva desafios e áreas para melhorar..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Alertas e Preocupações
                      </label>
                      <textarea
                        value={newSession.notes.alerts}
                        onChange={(e) => setNewSession({
                          ...newSession,
                          notes: { ...newSession.notes, alerts: e.target.value }
                        })}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                        placeholder="Anote quaisquer preocupações ou alertas..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Próximos Passos
                      </label>
                      <textarea
                        value={newSession.notes.nextSteps}
                        onChange={(e) => setNewSession({
                          ...newSession,
                          notes: { ...newSession.notes, nextSteps: e.target.value }
                        })}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                        placeholder="Descreva próximos passos e recomendações..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Feedback dos Pais
                      </label>
                      <textarea
                        value={newSession.notes.parentFeedback}
                        onChange={(e) => setNewSession({
                          ...newSession,
                          notes: { ...newSession.notes, parentFeedback: e.target.value }
                        })}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                        placeholder="Adicione qualquer feedback ou preocupação dos pais..."
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => setShowNewSessionForm(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-brand-700 text-white rounded-md text-sm font-medium hover:bg-brand-800"
                    >
                      Criar Sessão
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">
                  {selectedSession ? 'Editar Notas da Sessão' : 'Novas Notas da Sessão'}
                </h2>
                
                {selectedSession && (
                  <div className="mb-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Tipo de Sessão
                        </label>
                        <div className="px-3 py-2 border border-gray-200 rounded-md bg-gray-50">
                          {selectedSession.sessionType === 'residence' ? 'At Patient\'s Residence' : 'Video Conference'}
                        </div>
                      </div>
                      {selectedSession.sessionType === 'residence' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Local
                          </label>
                          <div className="px-3 py-2 border border-gray-200 rounded-md bg-gray-50">
                            {selectedSession.location || 'Local não especificado'}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Horário de Entrada
                        </label>
                        <div className="px-3 py-2 border border-gray-200 rounded-md bg-gray-50">
                          {selectedSession.checkInTime || 'Não registrado'}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Horário de Saída
                        </label>
                        <div className="px-3 py-2 border border-gray-200 rounded-md bg-gray-50">
                          {selectedSession.checkOutTime || 'Não registrado'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <form onSubmit={handleSubmitNote} className="space-y-4">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        O que deu certo
                      </label>
                      <textarea
                        value={sessionNotes.whatWentRight}
                        onChange={(e) => setSessionNotes({
                          ...sessionNotes,
                          whatWentRight: e.target.value
                        })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                        placeholder="Descreva sucessos e conquistas..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        O que deu errado
                      </label>
                      <textarea
                        value={sessionNotes.whatWentWrong}
                        onChange={(e) => setSessionNotes({
                          ...sessionNotes,
                          whatWentWrong: e.target.value
                        })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                        placeholder="Descreva desafios e áreas para melhorar..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Alertas e Preocupações
                      </label>
                      <textarea
                        value={sessionNotes.alerts}
                        onChange={(e) => setSessionNotes({
                          ...sessionNotes,
                          alerts: e.target.value
                        })}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                        placeholder="Anote quaisquer preocupações ou alertas..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Próximos Passos
                      </label>
                      <textarea
                        value={sessionNotes.nextSteps}
                        onChange={(e) => setSessionNotes({
                          ...sessionNotes,
                          nextSteps: e.target.value
                        })}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                        placeholder="Descreva próximos passos e recomendações..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Feedback dos Pais
                      </label>
                      <textarea
                        value={sessionNotes.parentFeedback}
                        onChange={(e) => setSessionNotes({
                          ...sessionNotes,
                          parentFeedback: e.target.value
                        })}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                        placeholder="Adicione qualquer feedback ou preocupação dos pais..."
                      />
                    </div>
                  </div>

                  <div className="flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedSession(null);
                        setSessionNotes({
                          whatWentRight: '',
                          whatWentWrong: '',
                          alerts: '',
                          nextSteps: '',
                          parentFeedback: ''
                        });
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={!selectedSession}
                      className="px-4 py-2 bg-brand-700 text-white rounded-md text-sm font-medium hover:bg-brand-800 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save size={16} className="mr-1" />
                      Salvar Notas
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content - Behaviors */}
      {activeTab === 'behaviors' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-medium text-gray-900">Acompanhamento de Comportamentos</h2>
              <button 
                onClick={() => navigate(`/dashboard/patients/${id}/behaviors/new`)}
                className="px-3 py-2 bg-brand-700 text-white rounded-lg text-sm font-medium flex items-center"
              >
                <Plus size={16} className="mr-1" />
                Adicionar Comportamento
              </button>
            </div>

            {/* Behavior Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-brand-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-brand-500">Total de Registros</p>
                    <p className="text-2xl font-semibold text-brand-700">
                      {patient?.behaviorRecords?.length || 0}
                    </p>
                  </div>
                  <Activity size={24} className="text-brand-600" />
                </div>
              </div>
              <div className="bg-red-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-500">Alta Intensidade</p>
                    <p className="text-2xl font-semibold text-red-700">
                      {patient?.behaviorRecords?.filter(r => r.intensity >= 4).length || 0}
                    </p>
                  </div>
                  <AlertTriangle size={24} className="text-red-600" />
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-500">Duração Média</p>
                    <p className="text-2xl font-semibold text-green-700">
                      {patient?.behaviorRecords?.length ? 
                        Math.round(patient.behaviorRecords.reduce((acc, r) => acc + r.duration, 0) / patient.behaviorRecords.length) 
                        : 0} min
                    </p>
                  </div>
                  <Clock size={24} className="text-green-600" />
                </div>
              </div>
            </div>

            {/* Behaviors List */}
            <div className="space-y-4">
              {loading ? (
                <div className="text-center py-4">
                  <p className="text-gray-500">Carregando comportamentos...</p>
                </div>
              ) : error ? (
                <div className="text-center py-4">
                  <p className="text-red-500">{error}</p>
                </div>
              ) : patient?.behaviorRecords?.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-gray-500">Nenhum comportamento registrado ainda.</p>
                </div>
              ) : (
                patient?.behaviorRecords?.map((record) => (
                  <div key={record.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">{record.behavior.name}</h3>
                        <p className="text-sm text-gray-500">
                          Duração: {record.duration} minutos • Intensidade: {record.intensity}/5
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => navigate(`/dashboard/patients/${id}/behaviors/${record.id}`)}
                          className="text-brand-600 hover:text-brand-800"
                        >
                          Ver Detalhes
                        </button>
                      </div>
                    </div>
                    {record.antecedent && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">Antecedente:</p>
                        <p className="text-sm text-gray-700">{record.antecedent}</p>
                      </div>
                    )}
                    {record.consequence && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">Consequência:</p>
                        <p className="text-sm text-gray-700">{record.consequence}</p>
                      </div>
                    )}
                    {record.notes && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">Notas:</p>
                        <p className="text-sm text-gray-700">{record.notes}</p>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content - Observação */}
      {activeTab === 'observacao' && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Observações do Paciente</h2>
          <div className="flex space-x-4 mb-4 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700">Data inicial</label>
              <input type="date" value={obsStartDate} onChange={e => setObsStartDate(e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Data final</label>
              <input type="date" value={obsEndDate} onChange={e => setObsEndDate(e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Especialidade</label>
              <select
                value={obsSpecialtyFilter || ''}
                onChange={e => setObsSpecialtyFilter(e.target.value || null)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              >
                <option value="">Todas</option>
                <option value="Psicologia">Psicologia</option>
                <option value="Fonoaudiologia">Fonoaudiologia</option>
                <option value="Terapia Ocupacional">Terapia Ocupacional</option>
              </select>
            </div>
            <button
              className="ml-auto px-4 py-2 bg-brand-700 text-white rounded-md text-sm font-medium hover:bg-brand-800 disabled:opacity-50"
              onClick={async () => {
                setTriggerAnalysisLoading(true);
                setTriggerAnalysisError(null);
                try {
                  await triggerObservationAnalysis(id);
                  setActiveTab('analytics');
                  setAnalyticsTab('observacao');
                } catch (err) {
                  setTriggerAnalysisError(err.response?.data?.error || err.message);
                } finally {
                  setTriggerAnalysisLoading(false);
                }
              }}
              disabled={triggerAnalysisLoading}
            >
              {triggerAnalysisLoading ? 'Analisando...' : 'Análise'}
            </button>
          </div>
          {triggerAnalysisError && <div className="text-red-600 mb-2">{triggerAnalysisError}</div>}
          {obsLoading ? (
            <div>Carregando observações...</div>
          ) : obsError ? (
            <div className="text-red-600">{obsError}</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Especialidade</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Terapeuta</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Observação</th>
                </tr>
              </thead>
              <tbody>
                {observations.filter(obs => !obsSpecialtyFilter || obs.specialty === obsSpecialtyFilter).length === 0 ? (
                  <tr><td colSpan={4} className="text-center py-4 text-gray-500">Nenhuma observação encontrada.</td></tr>
                ) : (
                  observations.filter(obs => !obsSpecialtyFilter || obs.specialty === obsSpecialtyFilter).map(obs => (
                    <tr key={obs.id}>
                      <td className="px-4 py-2 whitespace-nowrap">{formatDateBR(obs.date)}</td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {obs.specialty ? (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            obs.specialty === 'Psicologia' ? 'bg-purple-100 text-purple-800' :
                            obs.specialty === 'Fonoaudiologia' ? 'bg-blue-100 text-blue-800' :
                            obs.specialty === 'Terapia Ocupacional' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {obs.specialty}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">{obs.therapist}</td>
                      <td className="px-4 py-2">{obs.observation_text}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Main Content - Analytics */}
      {activeTab === 'analytics' && (
        <AnalyticsExamples patientId={id} patientName={patient ? maskPatientName(`${patient.firstName} ${patient.lastName}`, patient.id) : ''} />
      )}
      {/* Main Content - Laudo */}
      {activeTab === 'laudo' && (
        <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
          {/* Header: filename + eye icon + export button */}
          {patient?.laudoFile ? (
            laudoFileUnavailable ? (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <AlertTriangle className="text-amber-600" size={20} />
                  <span className="text-amber-800 font-medium">Arquivo não disponível</span>
                </div>
                <p className="text-amber-700 text-sm">
                  O arquivo do laudo (<span className="font-mono">{patient.laudoFile}</span>) não foi encontrado no servidor.
                  Pode ter sido perdido durante uma atualização do sistema. Por favor, faça o upload novamente.
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Paperclip size={16} className="text-gray-400" />
                    <span className="font-mono">{patient.laudoFile}</span>
                    <Eye
                      size={18}
                      className="cursor-pointer text-gray-400 hover:text-brand-600 transition-colors"
                      onClick={async () => {
                        const available = await checkLaudoAvailability();
                        if (available) {
                          setPreviewZoom(1);
                          setShowPreview(true);
                        }
                      }}
                      title="Visualizar laudo"
                    />
                  </div>
                  {(patient?.laudo_analysis || patient?.laudo_score_report) && (
                    <button
                      type="button"
                      onClick={handleExportScoreReport}
                      disabled={exportingScoreReport}
                      className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <Download size={16} />
                      {exportingScoreReport ? 'Exportando...' : 'Exportar Laudo Completo'}
                    </button>
                  )}
                </div>

                {/* Stepper */}
                <LaudoStepper
                  patient={patient}
                  patientId={id}
                  onComplete={fetchPatientData}
                />

                {/* Results: Análise e Estimativa (collapsible) */}
                {patient?.laudo_analysis && (
                  <CollapsibleSection title="Análise e Estimativa" defaultOpen>
                    <div className="text-gray-700 text-sm whitespace-pre-wrap">
                      {patient.laudo_analysis}
                    </div>
                    {(() => {
                      const parsed = parseLaudoAnalysis(patient.laudo_analysis);
                      const validation = validateLaudoAnalysis(parsed);
                      if (validation.warnings.length === 0) return null;
                      return (
                        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mt-3">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="font-medium text-amber-800 mb-1">Avisos de Validação da Análise</p>
                              <ul className="text-sm text-amber-700 list-disc list-inside space-y-1">
                                {validation.warnings.map((w, i) => (
                                  <li key={i}>{w}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </CollapsibleSection>
                )}

                {/* Results: Avaliação da Qualidade (collapsible) */}
                {patient?.laudo_score_report && (
                  <CollapsibleSection title="Avaliação da Qualidade" defaultOpen>
                    <div className="space-y-6">
                      <div className="text-gray-700 text-sm">
                        <p className="mb-4">
                          Aqui está a avaliação do laudo médico utilizando a Escala Universal de Qualidade de Laudos – Avaliação de TEA, com a pontuação detalhada por domínio e subdomínio:
                        </p>
                      </div>

                      {/* Assessment Tabs */}
                      <div className="border-b border-gray-200">
                        <nav className="flex space-x-8">
                          {[
                            { key: 'overview', label: 'Visão Geral' },
                            { key: 'assessment', label: 'Avaliação' },
                            { key: 'references', label: 'Referências' }
                          ].map((tab) => (
                            <button
                              key={tab.key}
                              onClick={() => setAssessmentActiveTab(tab.key)}
                              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                                assessmentActiveTab === tab.key
                                  ? 'border-brand-500 text-brand-600'
                                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                              }`}
                            >
                              {tab.label}
                            </button>
                          ))}
                        </nav>
                      </div>

                      {/* Tab Content */}
                      {assessmentActiveTab === 'overview' && (
                        <div className="space-y-6">
                          <div>
                            <h2 className="text-xl font-semibold mb-4">Objetivo da Escala</h2>
                            <p className="text-gray-700 leading-relaxed mb-6">
                              Avaliar a qualidade de laudos clínicos de diagnóstico de TEA com base em seis domínios principais que refletem as melhores práticas clínicas, independentemente do modelo adotado (DSM-5, CID-11, diretrizes nacionais, protocolos clínicos validados como ADOS-2, CARS, etc.).
                            </p>
                          </div>

                          <div>
                            <h3 className="text-lg font-semibold mb-4">Sistema de Pontuação Geral (1 a 10)</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
                              <div className="bg-green-600 text-white p-3 rounded text-center font-medium">9-10: Excepcional</div>
                              <div className="bg-teal-500 text-white p-3 rounded text-center font-medium">7-8: Bom</div>
                              <div className="bg-yellow-500 text-gray-900 p-3 rounded text-center font-medium">5-6: Adequado</div>
                              <div className="bg-orange-500 text-white p-3 rounded text-center font-medium">3-4: Fraco</div>
                              <div className="bg-red-600 text-white p-3 rounded text-center font-medium">1-2: Inadequado</div>
                            </div>
                          </div>

                          <div>
                            <h3 className="text-lg font-semibold mb-4">Classificação Final (Pontuação Total)</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                              <div className="bg-green-600 text-white p-3 rounded text-center font-medium">90-100: Excepcional</div>
                              <div className="bg-teal-500 text-white p-3 rounded text-center font-medium">70-89: Bom</div>
                              <div className="bg-yellow-500 text-gray-900 p-3 rounded text-center font-medium">50-69: Adequado</div>
                              <div className="bg-orange-500 text-white p-3 rounded text-center font-medium">30-49: Fraco</div>
                              <div className="bg-red-600 text-white p-3 rounded text-center font-medium">0-29: Inadequado</div>
                            </div>
                          </div>
                        </div>
                      )}

                      {assessmentActiveTab === 'assessment' && (
                        <div className="space-y-6">
                          {/* Score Summary with calculated total */}
                          {(() => {
                            const parsed = parseScoreReport(patient.laudo_score_report);
                            const validation = validateScores(parsed);
                            const classification = getClassification(validation.total);
                            return (
                              <>
                                <div className="bg-gradient-to-r from-brand-700 to-brand-600 text-white p-6 rounded-lg text-center">
                                  <h2 className="text-xl font-semibold mb-2">Pontuação Total</h2>
                                  <div className="text-4xl font-bold mb-2">
                                    {validation.total}
                                  </div>
                                  <div className="mb-2">de 100 pontos</div>
                                  <div className="inline-block px-4 py-2 bg-white bg-opacity-20 rounded-full">
                                    {classification}
                                  </div>
                                </div>
                                {validation.warnings.length > 0 && (
                                  <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mt-3">
                                    <div className="flex items-start gap-2">
                                      <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                                      <div>
                                        <p className="font-medium text-amber-800 mb-1">Avisos de Validação</p>
                                        <ul className="text-sm text-amber-700 list-disc list-inside space-y-1">
                                          {validation.warnings.map((w, i) => (
                                            <li key={i}>{w}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </>
                            );
                          })()}

                          {/* LLM Assessment Data in Domain Format */}
                          <LLMQualityDomain
                            title="Domínio 1: Aplicação do Modelo Diagnóstico"
                            domainKey="1"
                            maxScore={25}
                            reportData={patient.laudo_score_report}
                            criteria={[
                              { key: '1.1', title: 'Critérios Diagnósticos Reconhecidos', maxPoints: 15 },
                              { key: '1.2', title: 'Classificação de Gravidade', maxPoints: 10 }
                            ]}
                          />

                          <LLMQualityDomain
                            title="Domínio 2: Qualidade da Avaliação Clínica"
                            domainKey="2"
                            maxScore={20}
                            reportData={patient.laudo_score_report}
                            criteria={[
                              { key: '2.1', title: 'Métodos de Avaliação', maxPoints: 12 },
                              { key: '2.2', title: 'Observações e Dados Clínicos', maxPoints: 8 }
                            ]}
                          />

                          <LLMQualityDomain
                            title="Domínio 3: Histórico do Desenvolvimento e Contexto"
                            domainKey="3"
                            maxScore={15}
                            reportData={patient.laudo_score_report}
                            criteria={[
                              { key: '3.1', title: 'Histórico do Desenvolvimento', maxPoints: 10 },
                              { key: '3.2', title: 'Informações Contextuais', maxPoints: 5 }
                            ]}
                          />

                          <LLMQualityDomain
                            title="Domínio 4: Perfil de Funcionamento Atual"
                            domainKey="4"
                            maxScore={15}
                            reportData={patient.laudo_score_report}
                            criteria={[
                              { key: '4.1', title: 'Avaliação Funcional', maxPoints: 10 },
                              { key: '4.2', title: 'Impacto na Vida Diária', maxPoints: 5 }
                            ]}
                          />

                          <LLMQualityDomain
                            title="Domínio 5: Recomendações de Intervenção"
                            domainKey="5"
                            maxScore={15}
                            reportData={patient.laudo_score_report}
                            criteria={[
                              { key: '5.1', title: 'Estratégias Baseadas em Evidências', maxPoints: 10 },
                              { key: '5.2', title: 'Coordenação de Serviços', maxPoints: 5 }
                            ]}
                          />

                          <LLMQualityDomain
                            title="Domínio 6: Clareza e Padronização do Laudo"
                            domainKey="6"
                            maxScore={10}
                            reportData={patient.laudo_score_report}
                            criteria={[
                              { key: '6.1', title: 'Qualidade do Documento', maxPoints: 5 },
                              { key: '6.2', title: 'Ações e Acompanhamento', maxPoints: 5 }
                            ]}
                          />

                          {/* Additional Analysis Sections */}
                          {patient.laudo_score_report.includes('Pontos Fortes') && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                              <h3 className="text-lg font-semibold text-green-800 mb-3">Pontos Fortes</h3>
                              <div className="text-green-700">
                                {(() => {
                                  const strengthsMatch = patient.laudo_score_report.match(/Pontos Fortes[:\s]*([\s\S]*?)(?=Pontos Fracos|Sugestões|$)/i);
                                  return strengthsMatch ? strengthsMatch[1].trim() : 'Não especificado';
                                })()}
                              </div>
                            </div>
                          )}

                          {patient.laudo_score_report.includes('Pontos Fracos') && (
                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
                              <h3 className="text-lg font-semibold text-orange-800 mb-3">Pontos Fracos</h3>
                              <div className="text-orange-700">
                                {(() => {
                                  const weaknessMatch = patient.laudo_score_report.match(/Pontos Fracos[:\s]*([\s\S]*?)(?=Sugestões|$)/i);
                                  return weaknessMatch ? weaknessMatch[1].trim() : 'Não especificado';
                                })()}
                              </div>
                            </div>
                          )}

                          {patient.laudo_score_report.includes('Sugestões') && (
                            <div className="bg-brand-50 border border-brand-200 rounded-lg p-6">
                              <h3 className="text-lg font-semibold text-brand-800 mb-3">Sugestões de Melhoria</h3>
                              <div className="text-brand-700">
                                {(() => {
                                  const suggestionsMatch = patient.laudo_score_report.match(/Sugestões[:\s]*([\s\S]*?)$/i);
                                  return suggestionsMatch ? suggestionsMatch[1].trim() : 'Não especificado';
                                })()}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {assessmentActiveTab === 'references' && (
                        <div className="space-y-6">
                          <h2 className="text-xl font-semibold">Fontes e Referências para Padrões de Qualidade</h2>

                          <div className="space-y-4">
                            <ReferenceSection
                              title="Diretrizes e Padrões Internacionais Primários"
                              items={[
                                {
                                  title: "Academia Americana de Pediatria (AAP) - Transtorno do Espectro Autista",
                                  url: "https://www.aafp.org/pubs/afp/issues/2020/1115/p629.html",
                                  description: "Diretrizes atualizadas para identificação, avaliação e manejo do transtorno do espectro autista."
                                },
                                {
                                  title: "NICE - Padrões de Qualidade QS51: Transtorno do Espectro Autista",
                                  url: "https://www.nice.org.uk/guidance/qs51",
                                  description: "Padrões de qualidade abrangentes cobrindo serviços de saúde e assistência social."
                                }
                              ]}
                            />

                            <ReferenceSection
                              title="Estrutura e Metodologia de Avaliação - AGREE II"
                              items={[
                                {
                                  title: "Brouwers, M.C., et al. (2010) - AGREE II: Avançando o Desenvolvimento de Diretrizes",
                                  url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC3001530/",
                                  description: "O artigo fundamental descrevendo o desenvolvimento e validação do instrumento AGREE II."
                                }
                              ]}
                            />

                            <ReferenceSection
                              title="Pesquisa de Avaliação de Qualidade Específica do Autismo"
                              items={[
                                {
                                  title: "Diretrizes de Prática Clínica para Transtornos do Espectro Autista em Crianças",
                                  url: "https://www.ncbi.nlm.nih.gov/pmc/articles/PMC9323940/",
                                  description: "Revisão sistemática e avaliação de qualidade de diretrizes usando AGREE II."
                                }
                              ]}
                            />
                          </div>

                          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                            <h4 className="font-semibold text-yellow-800 mb-2">Reconhecimento e Limitações</h4>
                            <p className="text-yellow-700 mb-2"><strong>Nota Importante:</strong> A escala de classificação de qualidade de avaliação do autismo apresentada foi sintetizada a partir de princípios gerais de avaliação clínica e das fontes listadas acima.</p>
                            <p className="text-yellow-700"><strong>Principais Limitações:</strong></p>
                            <ul className="list-disc list-inside text-yellow-700 mt-2 space-y-1">
                              <li>Nenhuma escala de classificação de qualidade de avaliação do autismo abrangente e validada foi encontrada na literatura</li>
                              <li>A maioria das avaliações de qualidade usa o instrumento geral AGREE II</li>
                              <li>O sistema de pontuação foi derivado de princípios de avaliação clínica</li>
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  </CollapsibleSection>
                )}
              </>
            )
          ) : (
            <div className="text-gray-500 text-sm">Nenhum laudo original enviado para este paciente.</div>
          )}
        </div>
      )}

      {/* Main Content - Avaliações */}
      {activeTab === 'avaliacoes' && (
        <AvaliacoesTab
          patientId={id}
          assessmentData={assessmentDashboardData}
          loading={assessmentDashboardLoading}
        />
      )}

      {/* Main Content - Relatórios de Auditoria */}
      {activeTab === 'auditReports' && (
        <AuditReportsTab patientId={id} />
      )}

      {/* Preview Modal */}
      <PreviewModal
        filename={patient?.laudoFile}
        onClose={() => setShowPreview(false)}
      />

      {/* Export Password Dialog */}
      <ExportPasswordDialog
        isOpen={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        onExport={doExportScoreReport}
        title="Exportar Laudo Completo"
        isLoading={exportingScoreReport}
      />

    </div>
  );
};

export default PatientDashboard; 